/**
 * Customers template ESM (Phase 10).
 *
 * Replaces theme/js/templates/customers.js. Three independent toggles
 * across the customer pages, all wired through one delegated `click`
 * listener on `document`:
 *
 *   1. `customers/addresses` — `.js-add-new-address-button` toggles the
 *      Shopify-generated `#address_form_new` form. aria-expanded tracks
 *      open/closed state; the trigger label swaps between its
 *      `data-add-text` and `data-cancel-text`.
 *
 *   2. `customer-address-card` snippet — `.js-edit-address-button`
 *      (aria-expanded) toggles the read-only `.js-address-info` block
 *      against the Shopify-generated edit form, scoped to the enclosing
 *      `.js-customer-address-card`. `.js-cancel-address-button` (inside
 *      the edit form) delegates back to the edit trigger so
 *      aria-expanded stays authoritative.
 *
 *   3. `customers/account` — `.js-load-more-orders` hides the wrap
 *      (`.load-more-orders-wrap`) and reveals `#more-orders` inside
 *      `#account-orders`.
 *
 * Both forms and `#more-orders` are hidden by default in SCSS, so the
 * "show" path sets inline `display: block` to win over the stylesheet.
 *
 * The IIFE assigned a country-select local that nothing read; dropped
 * here.
 *
 * Source path: theme/js/templates/customers.esm.js
 * Built name : ./customers.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-04
 * Model: Claude Opus 4.7
 * Updated: 2026-05-08 (Claude Opus 4.7) - aria-expanded state tracking; cancel button split from edit trigger
 *
 * [Customers]
 */

import { log } from './log.js';

const NEW_ADDRESS_TRIGGER = '.js-add-new-address-button';
const NEW_ADDRESS_FORM_ID = 'address_form_new';
const EDIT_ADDRESS_TRIGGER = '.js-edit-address-button';
const CANCEL_ADDRESS_TRIGGER = '.js-cancel-address-button';
const EDIT_ADDRESS_FORM_SELECTOR = 'form[id*="address_form_"]';
const ADDRESS_CARD_SELECTOR = '.js-customer-address-card';
const ADDRESS_INFO_SELECTOR = '.js-address-info';
const MORE_ORDERS_TRIGGER = '.js-load-more-orders';
const MORE_ORDERS_WRAP_SELECTOR = '.load-more-orders-wrap';
const ACCOUNT_ORDERS_ID = 'account-orders';
const MORE_ORDERS_ID = 'more-orders';

function show(el, displayValue = 'block') {
  if (el) el.style.display = displayValue;
}

function hide(el) {
  if (el) el.style.display = 'none';
}

function toggleAriaExpanded(trigger) {
  const expanded = trigger.getAttribute('aria-expanded') === 'true';
  trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
  return !expanded;
}

function handleNewAddressClick(trigger) {
  const form = document.getElementById(NEW_ADDRESS_FORM_ID);
  if (!form) return;
  const opening = toggleAriaExpanded(trigger);
  if (opening) {
    show(form);
    const cancel = trigger.getAttribute('data-cancel-text');
    if (cancel != null) trigger.textContent = cancel;
  } else {
    hide(form);
    const add = trigger.getAttribute('data-add-text');
    if (add != null) trigger.textContent = add;
  }
}

function handleEditAddressClick(trigger) {
  const card = trigger.closest(ADDRESS_CARD_SELECTOR);
  if (!card) return;
  const info = card.querySelector(ADDRESS_INFO_SELECTOR);
  const form = card.querySelector(EDIT_ADDRESS_FORM_SELECTOR);
  if (!form) return;
  const opening = toggleAriaExpanded(trigger);
  if (opening) {
    hide(info);
    show(form);
  } else {
    show(info);
    hide(form);
  }
}

function handleCancelAddressClick(trigger) {
  const card = trigger.closest(ADDRESS_CARD_SELECTOR);
  if (!card) return;
  const editTrigger = card.querySelector(EDIT_ADDRESS_TRIGGER);
  if (!editTrigger) return;
  const info = card.querySelector(ADDRESS_INFO_SELECTOR);
  const form = card.querySelector(EDIT_ADDRESS_FORM_SELECTOR);
  editTrigger.setAttribute('aria-expanded', 'false');
  show(info);
  hide(form);
}

function handleMoreOrdersClick(trigger) {
  const wrap = trigger.closest(MORE_ORDERS_WRAP_SELECTOR);
  const accountOrders = trigger.closest(`#${ACCOUNT_ORDERS_ID}`);
  if (!accountOrders) return;
  const more = accountOrders.querySelector(`#${MORE_ORDERS_ID}`);
  hide(wrap);
  show(more);
}

function handleClick(event) {
  const newAddress = event.target.closest(NEW_ADDRESS_TRIGGER);
  if (newAddress) {
    event.preventDefault();
    handleNewAddressClick(newAddress);
    return;
  }
  const editAddress = event.target.closest(EDIT_ADDRESS_TRIGGER);
  if (editAddress) {
    handleEditAddressClick(editAddress);
    return;
  }
  const cancelAddress = event.target.closest(CANCEL_ADDRESS_TRIGGER);
  if (cancelAddress) {
    handleCancelAddressClick(cancelAddress);
    return;
  }
  const moreOrders = event.target.closest(MORE_ORDERS_TRIGGER);
  if (moreOrders) {
    event.preventDefault();
    handleMoreOrdersClick(moreOrders);
  }
}

document.addEventListener('click', handleClick);

log('[Customers] registered');
